import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import { useTeam } from '../contexts/TeamContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { providerAPI } from '../services/api';
import { toast } from 'sonner';
import { Plus, Users, Phone, MapPin, Building, Star, Calendar } from 'lucide-react';

const Providers = () => {
  const { currentTeam } = useTeam();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentTeam?.id) {
      loadProviders();
    }
  }, [currentTeam?.id]); // Only re-run when team ID changes

  const loadProviders = async () => {
    try {
      // Only show loading on initial load
      if (providers.length === 0) {
        setLoading(true);
      }
      const response = await providerAPI.getAll(currentTeam.id);
      setProviders(response.data);
    } catch (error) {
      console.error('Error loading providers:', error);
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  // Group providers by dashboard area
  const groupedProviders = providers.reduce((groups, provider) => {
    const area = provider.dashboard_area || 'other';
    if (!groups[area]) groups[area] = [];
    groups[area].push(provider);
    return groups;
  }, {});

  const dashboardAreas = [
    { key: 'medical', label: 'Medical', icon: '🏥' },
    { key: 'legal', label: 'Legal', icon: '⚖️' },
    { key: 'financial', label: 'Financial', icon: '💰' },
    { key: 'caregiving', label: 'Caregiving', icon: '❤️' },
    { key: 'household', label: 'Household', icon: '🏠' },
    { key: 'other', label: 'Other', icon: '📋' }
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in" data-testid="providers-page">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-4xl font-bold">Provider Directory</h1>
          <div className="flex gap-3">
            <CreateProviderDialog onSuccess={loadProviders} />
            <QuickActions position="top" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {dashboardAreas.map(area => {
            const areaProviders = groupedProviders[area.key] || [];
            return (
              <Card key={area.key} className="glass-effect p-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{area.icon}</span>
                  <div>
                    <h3 className="font-semibold">{area.label}</h3>
                    <p className="text-sm text-gray-600">{areaProviders.length} providers</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading providers...</div>
        ) : providers.length === 0 ? (
          <Card className="glass-effect p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-2xl font-bold mb-2">No Providers Yet</h2>
            <p className="text-gray-600 mb-6">
              Start building your provider directory to keep all contact information organized.
            </p>
            <CreateProviderDialog onSuccess={loadProviders} trigger={
              <Button data-testid="create-first-provider-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Provider
              </Button>
            } />
          </Card>
        ) : (
          <div className="space-y-8">
            {dashboardAreas.map(area => {
              const areaProviders = groupedProviders[area.key] || [];
              if (areaProviders.length === 0) return null;
              
              return (
                <div key={area.key}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{area.icon}</span>
                    <h2 className="text-2xl font-bold">{area.label} Providers</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    {areaProviders.map(provider => (
                      <ProviderCard key={provider.id} provider={provider} onUpdate={loadProviders} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Quick Actions - Bottom */}
        <div className="mt-8">
          <QuickActions position="bottom" />
        </div>
      </div>
    </Layout>
  );
};

const ProviderCard = ({ provider, onUpdate }) => {
  const [contacts, setContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    loadProviderDetails();
  }, [provider.id]);

  const loadProviderDetails = async () => {
    try {
      setLoadingDetails(true);
      const [contactsRes, locationsRes] = await Promise.all([
        providerAPI.getContacts(provider.id),
        providerAPI.getLocations(provider.id)
      ]);
      
      setContacts(contactsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Error loading provider details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const primaryContact = contacts.find(c => c.is_primary) || contacts[0];
  const primaryLocation = locations.find(l => l.is_primary) || locations[0];

  return (
    <Card className="glass-effect p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xl font-semibold mb-1">{provider.name}</h3>
          <p className="text-sm text-gray-600">{provider.provider_type}</p>
          {provider.is_platform_user && (
            <Badge className="mt-2 bg-blue-100 text-blue-800">
              <Star className="w-3 h-3 mr-1" />
              Platform User
            </Badge>
          )}
        </div>
        <Badge variant="outline" className="capitalize">
          {provider.dashboard_area}
        </Badge>
      </div>

      {provider.notes && (
        <p className="text-sm text-gray-600 mb-4 italic">{provider.notes}</p>
      )}

      {/* Primary Contact */}
      {primaryContact && (
        <div className="border-t pt-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Phone className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-sm">Primary Contact</span>
          </div>
          <div className="ml-6">
            <p className="font-medium">{primaryContact.name}</p>
            {primaryContact.title && (
              <p className="text-sm text-gray-600">{primaryContact.title}</p>
            )}
            <div className="flex gap-4 text-sm text-gray-600 mt-1">
              {primaryContact.phone && <span>{primaryContact.phone}</span>}
              {primaryContact.email && <span>{primaryContact.email}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Primary Location */}
      {primaryLocation && (
        <div className="border-t pt-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-sm">Primary Location</span>
          </div>
          <div className="ml-6">
            <p className="font-medium">{primaryLocation.location_name}</p>
            <p className="text-sm text-gray-600">
              {primaryLocation.street && `${primaryLocation.street}, `}
              {primaryLocation.city && `${primaryLocation.city}, `}
              {primaryLocation.state} {primaryLocation.zip_code}
            </p>
          </div>
        </div>
      )}

      {/* Additional Details */}
      <div className="flex justify-between items-center pt-4 border-t">
        <div className="text-xs text-gray-500">
          {contacts.length > 1 && <span>{contacts.length} contacts</span>}
          {locations.length > 1 && <span className="ml-2">{locations.length} locations</span>}
        </div>
        <div className="flex gap-2">
          <AddContactDialog providerId={provider.id} onSuccess={loadProviderDetails} />
          <AddLocationDialog providerId={provider.id} onSuccess={loadProviderDetails} />
        </div>
      </div>
    </Card>
  );
};

const CreateProviderDialog = ({ onSuccess, trigger }) => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    provider_type: '',
    dashboard_area: 'medical',
    is_platform_user: false,
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await providerAPI.create(currentTeam.id, formData);
      toast.success('Provider added!');
      setOpen(false);
      setFormData({
        name: '',
        provider_type: '',
        dashboard_area: 'medical',
        is_platform_user: false,
        notes: ''
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating provider:', error);
      toast.error('Failed to add provider');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" data-testid="add-provider-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Provider
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Provider</DialogTitle>
          <DialogDescription>Add a healthcare provider, service provider, or professional to your directory.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Provider Name *</Label>
            <Input
              id="name"
              data-testid="provider-name-input"
              placeholder="e.g., Dr. Sarah Johnson"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="type">Provider Type *</Label>
            <Input
              id="type"
              data-testid="provider-type-input"
              placeholder="e.g., Primary Care Physician, Home Health Agency"
              value={formData.provider_type}
              onChange={(e) => setFormData({ ...formData, provider_type: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="area">Dashboard Area</Label>
            <select
              id="area"
              data-testid="provider-area-select"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={formData.dashboard_area}
              onChange={(e) => setFormData({ ...formData, dashboard_area: e.target.value })}
            >
              <option value="medical">Medical</option>
              <option value="legal">Legal</option>
              <option value="financial">Financial</option>
              <option value="caregiving">Caregiving</option>
              <option value="household">Household</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              data-testid="provider-notes-input"
              placeholder="Additional notes about this provider"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="platform_user"
              data-testid="provider-platform-input"
              checked={formData.is_platform_user}
              onChange={(e) => setFormData({ ...formData, is_platform_user: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="platform_user" className="text-sm">
              This provider is also a Pressed user
            </Label>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
            data-testid="provider-submit-btn"
          >
            {loading ? 'Adding...' : 'Add Provider'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AddContactDialog = ({ providerId, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    contact_type: 'primary',
    name: '',
    title: '',
    phone: '',
    email: '',
    extension: '',
    notes: '',
    is_primary: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await providerAPI.addContact(providerId, formData);
      toast.success('Contact added!');
      setOpen(false);
      setFormData({
        contact_type: 'primary',
        name: '',
        title: '',
        phone: '',
        email: '',
        extension: '',
        notes: '',
        is_primary: false
      });
      onSuccess();
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="add-contact-btn">
          <Phone className="w-4 h-4 mr-1" />
          Contact
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Contact</DialogTitle>
          <DialogDescription>Add contact information for this provider.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact_name">Name *</Label>
              <Input
                id="contact_name"
                data-testid="contact-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                data-testid="contact-title-input"
                placeholder="e.g., Office Manager"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                data-testid="contact-phone-input"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="extension">Extension</Label>
              <Input
                id="extension"
                data-testid="contact-extension-input"
                value={formData.extension}
                onChange={(e) => setFormData({ ...formData, extension: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="contact-email-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="contact_type">Contact Type</Label>
            <select
              id="contact_type"
              data-testid="contact-type-select"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={formData.contact_type}
              onChange={(e) => setFormData({ ...formData, contact_type: e.target.value })}
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="emergency">Emergency</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_primary_contact"
              data-testid="contact-primary-input"
              checked={formData.is_primary}
              onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="is_primary_contact" className="text-sm">
              Mark as primary contact
            </Label>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
            data-testid="contact-submit-btn"
          >
            {loading ? 'Adding...' : 'Add Contact'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AddLocationDialog = ({ providerId, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    location_name: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    website: '',
    parking_notes: '',
    accessibility_notes: '',
    office_staff_notes: '',
    is_primary: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await providerAPI.addLocation(providerId, formData);
      toast.success('Location added!');
      setOpen(false);
      setFormData({
        location_name: '',
        street: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'USA',
        website: '',
        parking_notes: '',
        accessibility_notes: '',
        office_staff_notes: '',
        is_primary: false
      });
      onSuccess();
    } catch (error) {
      console.error('Error adding location:', error);
      toast.error('Failed to add location');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="add-location-btn">
          <MapPin className="w-4 h-4 mr-1" />
          Location
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Location</DialogTitle>
          <DialogDescription>Add a location for this provider.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="location_name">Location Name *</Label>
            <Input
              id="location_name"
              data-testid="location-name-input"
              placeholder="e.g., Main Office, Downtown Clinic"
              value={formData.location_name}
              onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="street">Street Address</Label>
            <Input
              id="street"
              data-testid="location-street-input"
              placeholder="123 Main Street"
              value={formData.street}
              onChange={(e) => setFormData({ ...formData, street: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                data-testid="location-city-input"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                data-testid="location-state-input"
                placeholder="CA"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="zip">ZIP Code</Label>
            <Input
              id="zip"
              data-testid="location-zip-input"
              placeholder="90210"
              value={formData.zip_code}
              onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="url"
              data-testid="location-website-input"
              placeholder="https://example.com"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="parking">Parking Notes</Label>
            <Textarea
              id="parking"
              data-testid="location-parking-input"
              placeholder="Parking information, cost, accessibility"
              value={formData.parking_notes}
              onChange={(e) => setFormData({ ...formData, parking_notes: e.target.value })}
              rows={2}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_primary_location"
              data-testid="location-primary-input"
              checked={formData.is_primary}
              onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="is_primary_location" className="text-sm">
              Mark as primary location
            </Label>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
            data-testid="location-submit-btn"
          >
            {loading ? 'Adding...' : 'Add Location'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Providers;
